import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { logger } from '@app/core/helpers/logger';
import { LibraryRouter } from '@app/models/library.router';
import { videoType, vimeoCoverImageUrl, youtubeCoverImageUrl } from '@core/helpers/video';
import {
  ContentService,
  ContextService,
  FilesService,
  MetaService,
  PlatformService,
  RatingService,
  ScreenService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { TranslateService } from '@ngx-translate/core';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  Content,
  ContentTypeEnum,
  getContentInstanceByItem,
  IContentPopularityParams,
  IContentStats,
  IImageFile,
  ImageSizeEnum,
  LessonContentType,
  Text,
  Video as VideoContent,
  Vocabulary,
} from 'lingo2-models';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import striptags from 'striptags';
import { ArticleCardComponent } from './article-card/article-card.component';
import { BaseContentCard, ContentCardDisplayMode } from './base-content-card';
import { LessonCardComponent } from './lesson-card/lesson-card.component';
import { MediaCardComponent } from './media-card/media-card.component';
import { MicroTextCardComponent } from './microtext-card/microText-card.component';
import { VocabularyCardComponent } from './vocabulary-card/vocabulary-card.component';

@Directive({
  // tslint:disable-next-line: directive-selector
  selector: '[content-data]',
})
export class ContentDataDirective {
  public constructor(public viewContainerRef: ViewContainerRef) {}
}

@Component({
  selector: 'app-content-card',
  templateUrl: './content-card.component.html',
  styleUrls: ['./content-card.component.scss'],
})
export class ContentCardComponent extends ChangableComponent implements OnInit, OnDestroy {
  @ViewChild(ContentDataDirective, { static: true }) public contentCard: ContentDataDirective;
  @Input() public set item_id(id: string) {
    this._item_id = id;
    this.loadItem();
    // this.loadComponent();
  }
  @Input() public set item(item: Content) {
    this._item = getContentInstanceByItem(item);
    this.loadComponent();
  }
  public get item() {
    return this._item;
  }
  @Input() public set show(show: boolean) {
    this._show = show;
    if (this.componentInstance) {
      this.componentInstance.show = show;
    }
  }
  @Input() public set canClose(canClose: boolean) {
    this._canClose = canClose;
    if (this.componentInstance) {
      this.componentInstance.canClose = canClose;
    }
  }
  @Input() public set inline(inline: boolean) {
    this._inline = inline;
    if (this.componentInstance) {
      this.componentInstance.inline = inline;
    }
  }
  @Input() public set expanded(expanded: boolean) {
    this._expanded = expanded;
    if (this.componentInstance && this.componentInstance instanceof ArticleCardComponent) {
      this.componentInstance.expanded = expanded;
    }
  }
  @Input() public set active(active: boolean) {
    this._active = active;
    if (this.componentInstance) {
      this.componentInstance.active = active;
    }
  }
  @Input() public set cut(cut: boolean) {
    this._cut = cut;
    if (this.componentInstance) {
      this.componentInstance.cut = cut;
    }
  }
  @Input() public set meta(meta: boolean) {
    this._meta = meta;
    // if (this.componentInstance) {
    //   this.componentInstance.cut = cut;
    // }
    if (this._meta) {
      this.setupMeta();
    } else {
      this.resetMeta();
    }
  }
  @Input() public viewportChange = true;
  @Input() public playAutomatically: boolean;
  @Input() public fromCollection: boolean;
  @Input() public type: ContentTypeEnum;
  @Input() public showSkeleton: boolean = true;
  @Input() public set displayMode(displayMode: ContentCardDisplayMode) {
    this._displayMode = displayMode;
    this.loadComponent();
  }

  // tslint:disable-next-line: no-output-native
  @Output() public close = new EventEmitter<any>();
  @Output() public favoriteChanged = new EventEmitter<boolean>();
  public loading = false;

  private _item_id: string;
  private _item: Content;
  private _show = true;
  private _canClose = false;
  private _inline = false;
  private _expanded = false;
  private _active = false;
  private _cut = true;
  private _meta = false;
  private _originalMeta: any;
  private viewProcessed = false;
  private inViewportTopChange = this.register(new Subject<boolean>());
  private inViewportBottomChange = this.register(new Subject<boolean>());
  private inViewportTop = false;
  private inViewportBottom = false;
  private viewIncrementTimer;
  private componentInstance: BaseContentCard;
  private _displayMode: ContentCardDisplayMode = 'default';

  // #region for debug
  public debug$ = this.contextService.debug$;
  public debugPopularity: number;
  public debugPopularityByPeriod: number;
  public debugParams: IContentPopularityParams;
  public debugStats: Partial<IContentStats>;
  // #endregion

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    private contentService: ContentService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private screenService: ScreenService,
    private router: Router,
    private metaService: MetaService,
    private translate: TranslateService,
    protected cdr: ChangeDetectorRef,
    protected platform: PlatformService,
    private readonly contextService: ContextService,
    private readonly ratingService: RatingService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit() {
    this.screenService.enableExtraFonts();
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.resetMeta();
  }

  protected loadItem() {
    if (!this._item_id) {
      return;
    }
    this.loading = true;
    this.detectChanges();

    this.contentService
      .getById(this._item_id, true)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (item) => {
          this.loading = false;
          this._item = item;
          this.loadComponent();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loading = false;
        },
      });
  }

  protected loadComponent() {
    if (!this.item) {
      return;
    }

    const component = this.getComponentByType(this._item.type);
    if (!component) {
      logger.error('Not supported <content-card> form ' + this._item.type);
      return;
    }

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);

    const viewContainerRef = this.contentCard.viewContainerRef;
    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent(componentFactory);
    this.componentInstance = componentRef.instance as any;
    this.componentInstance.item = this._item;
    this.componentInstance.show = this._show;
    this.componentInstance.canClose = this._canClose;
    this.componentInstance.displayMode = this._displayMode;
    this.componentInstance.inline = this._inline;
    this.componentInstance.active = this._active;
    this.componentInstance.cut = this._cut;
    this.componentInstance.playAutomatically = this.playAutomatically;
    this.componentInstance.fromCollection = this.fromCollection;
    if (this.componentInstance instanceof ArticleCardComponent) {
      this.componentInstance.expanded = this._expanded;
    }

    this.componentInstance.close.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (event) => this.onClose(event),
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
    this.componentInstance.favoriteChanged.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (event) => this.favoriteChanged.emit(event),
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    if (this._meta) {
      this.setupMeta();
    }
    this.initViewportCheck();
    this.debug$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (debug) => {
        if (debug) {
          this.reloadRating();
        }
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
    this.markForCheck();
  }

  public reloadRating() {
    this.ratingService
      .getContentRating(this._item.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (data) => {
          this.debugPopularity = data.popularity;
          this.debugPopularityByPeriod = data.popularity_by_period;
          this.debugParams = data.params;
          this.debugStats = data.stats;
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected initViewportCheck() {
    this.inViewportTopChange
      .pipe(debounceTime(300))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (inViewportTop: boolean) => {
          this.inViewportTop = this.inViewportTop || inViewportTop;
          this.checkIsFullInViewport();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.inViewportBottomChange
      .pipe(debounceTime(300))
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (inViewportBottom: boolean) => {
          this.inViewportBottom = this.inViewportBottom || inViewportBottom;
          this.checkIsFullInViewport();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  /**
   * @see LibraryModule.entryComponents
   */
  protected getComponentByType(type: ContentTypeEnum): any {
    switch (type) {
      case ContentTypeEnum.article:
      case ContentTypeEnum.news:
        return ArticleCardComponent;

      case ContentTypeEnum.microtext:
        return MicroTextCardComponent;

      case ContentTypeEnum.video:
      case ContentTypeEnum.audio:
      case ContentTypeEnum.image:
        return MediaCardComponent;

      // case ContentTypeEnum.document:
      //   return DocumentCardComponent;

      case ContentTypeEnum.vocabulary:
        return VocabularyCardComponent;
    }

    if (LessonContentType.includes(type)) {
      return LessonCardComponent;
    }
  }

  public onClose(value) {
    this.close.emit(value);
  }

  onInViewportTopChange(inViewport) {
    if (this.viewportChange) {
      this.inViewportTopChange.next(inViewport.visible);
    }
  }

  onInViewportBottomChange(inViewport) {
    if (this.viewportChange) {
      this.inViewportBottomChange.next(inViewport.visible);
    }
  }

  protected checkIsFullInViewport() {
    // TODO переписать логику, тут часто срабатывает инкремент даже если я не смотрел 2 секунды, а просто мимо пролистал
    this.onBrowserOnly(() => {
      this.clearTimeout(this.viewIncrementTimer);
      // если я 'видел' начало или конец контента, и смотрел на него не менее 2 секунд, то +1 количество просмотров
      if ((this.inViewportTop || this.inViewportBottom) && !this.viewProcessed) {
        // logger.debug('incrementView timer init');
        this.viewIncrementTimer = this.setTimeout(() => {
          // logger.debug('incrementView timer fired');
          this.incrementView();
        }, 100);
      }
    });
  }

  protected incrementView() {
    if (this.viewProcessed) {
      return;
    }
    this.contentService
      .addView(this._item.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this._item.stats = {
            ...this._item.stats,
            views_count: response.view_count,
          };
          this.contentService.change.next({
            id: this._item.id,
            stats: this._item.stats,
          });
          this.viewProcessed = true;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected get metaImage(): IImageFile {
    if (this.item && !this.item.cover_id && this.item.type === ContentTypeEnum.video) {
      const _video = this._item as VideoContent;
      const _type = videoType(_video.media_url);
      let _metaImageUrl: string;
      switch (_type) {
        case 'youtube':
          _metaImageUrl = youtubeCoverImageUrl(_video.media_url, 'maxresdefault');
          if (_metaImageUrl) {
            return {
              url: _metaImageUrl,
              size: ImageSizeEnum.lg,
              width: 1280,
              height: 720,
            };
          }
          break;

        case 'vimeo':
          _metaImageUrl = vimeoCoverImageUrl(_video.media_url);
          if (_metaImageUrl) {
            return {
              url: _metaImageUrl,
              size: ImageSizeEnum.lg,
              width: null,
              height: null,
            };
          }
          break;
      }
      return;
    }

    if (this.item && this.item.cover_id) {
      return { url: FilesService.getFileUrlBySize(this.item.cover_id, ImageSizeEnum.lg), size: ImageSizeEnum.lg };
    }
    return null;
  }

  protected setupMeta() {
    if (!this.item) {
      return;
    }

    if (!this._originalMeta) {
      this._originalMeta = {
        title: this.metaService.getTitle(),
      };
    }

    this.metaService.setTitle([this.contentService.contentFullTitle(this.item)]);

    const description: string[] = [];
    if ((this.item.description || '').length) {
      description.push(this.item.description);
    }

    switch (this.item.type) {
      case ContentTypeEnum.article:
      case ContentTypeEnum.news:
        const textItem = this.item as any as Text;
        const text = striptags(textItem.text || '').substr(0, 1024);
        if (text.length) {
          description.push(`${text}...`);
        }
        this.metaService.setTypeArticle();
        break;

      case ContentTypeEnum.vocabulary:
        const vocabularyItem = this.item as any as Vocabulary;
        const terms = (vocabularyItem.terms || [])
          .map((t) => ({ term: striptags(t.term || ''), definition: striptags(t.definition || '') }))
          .filter((t) => t.term.length > 0)
          .map((t) => t.term + (t.definition.length ? ` (${t.definition})` : ''));
        if (terms.length > 0) {
          const _t = this.translate.instant('library.meta-vocabulary-terms');
          description.push(_t + ': ' + terms.join(', ').substr(0, 1024));
        }
        this.metaService.setTypeArticle();
        break;

      default:
        this.metaService.setTypeArticle();
    }
    this.metaService.setDescription(description.join('. '));

    const contentRoute = LibraryRouter.contentRouteUniversal(this.item);
    this.metaService.setUrl(contentRoute);

    const image = this.metaImage;
    if (image) {
      this.metaService.setImage(image.url, '', 1 * (image.width as any), 1 * (image.height as any));
    }
  }

  protected resetMeta() {
    if (this._originalMeta) {
      this.metaService.setTitle([this._originalMeta.title]);
    }
  }
}
